import { defineComponent as _defineComponent } from 'vue';
import { BAlert, BButton, BFormCheckbox, BModal } from "bootstrap-vue";
import { computed, onMounted, ref, watch } from "vue";
import { RouterLink } from "vue-router";
import { exportHistoryToFileSource, fetchHistoryExportRecords } from "@/api/histories.export";
import { DEFAULT_EXPORT_PARAMS } from "@/composables/shortTermStorage";
import { useTaskMonitor } from "@/composables/taskMonitor";
import ExportRecordCard from "./ExportRecordCard.vue";
import ExportToFileSourceForm from "@/components/Common/ExportForm.vue";
import ExportToRDMRepositoryForm from "@/components/Common/ExportRDMForm.vue";
import ExternalLink from "@/components/ExternalLink.vue";
import LoadingSpan from "@/components/LoadingSpan.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'HistoryArchiveExportSelector',
    props: {
        history: null
    },
    emits: ["onArchive"],
    setup(__props, { emit }) {
        const props = __props;
        const { isRunning: isExportTaskRunning, waitForTask, requestHasFailed: taskMonitorRequestFailed, hasFailed: taskHasFailed, } = useTaskMonitor();
        const existingExports = ref([]);
        const isLoading = ref(true);
        const isExportingRecord = ref(false);
        const isExportDialogOpen = ref(false);
        const isDeleteContentsConfirmed = ref(false);
        const exportErrorMessage = ref(null);
        const historyName = computed(() => {
            return props.history.name;
        });
        const mostUpToDateExport = computed(() => {
            return existingExports.value.find((exportRecord) => exportRecord.isPermanent && (exportRecord.isUpToDate || exportRecord.isPreparing));
        });
        const canCreateExportRecord = computed(() => {
            return !isLoading.value && !isExportTaskRunning.value && !mostUpToDateExport.value;
        });
        const canArchiveHistory = computed(() => {
            return (!isLoading.value && !isExportTaskRunning.value && mostUpToDateExport.value && isDeleteContentsConfirmed.value);
        });
        const mostUpToDateExportIsReady = computed(() => {
            return mostUpToDateExport.value && mostUpToDateExport.value.isReady && mostUpToDateExport.value.canReimport;
        });
        onMounted(async () => {
            isLoading.value = true;
            await updateExports();
            isLoading.value = false;
        });
        watch(isExportTaskRunning, (newValue, oldValue) => {
            isExportingRecord.value = newValue;
            const hasFinished = oldValue && !newValue;
            if (hasFinished) {
                updateExports();
            }
        });
        async function updateExports() {
            exportErrorMessage.value = null;
            try {
                existingExports.value = await fetchHistoryExportRecords(props.history.id);
                if (mostUpToDateExport.value) {
                    const shouldWaitForTask = mostUpToDateExport.value?.isPreparing &&
                        !isExportTaskRunning.value &&
                        !taskMonitorRequestFailed.value &&
                        !taskHasFailed.value;
                    if (shouldWaitForTask) {
                        waitForTask(mostUpToDateExport.value.taskUUID);
                    }
                    if (taskMonitorRequestFailed.value) {
                        exportErrorMessage.value = "The request to get the export progress failed. Please check back later.";
                    }
                    if (taskHasFailed.value) {
                        exportErrorMessage.value = "The history export request failed. Please try again later.";
                    }
                }
            }
            catch (e) {
                exportErrorMessage.value = "The request to get your history exports records failed. Please check back later.";
            }
        }
        async function onCreateExportRecord() {
            isExportDialogOpen.value = true;
        }
        async function doExportToFileSourceWithPrefix(exportDirectory, fileName) {
            // Avoid name collisions if multiple different histories are exported to the
            // same destination with the same name
            const fileNameCompatibleUpdateTime = props.history.update_time.replace(/:/g, "-");
            const prefixedFileName = `${fileNameCompatibleUpdateTime}_${fileName}`;
            doExportToFileSource(exportDirectory, prefixedFileName);
        }
        async function doExportToFileSource(exportDirectory, fileName) {
            isExportingRecord.value = true;
            isExportDialogOpen.value = false;
            try {
                await exportHistoryToFileSource(props.history.id, exportDirectory, fileName, DEFAULT_EXPORT_PARAMS);
            }
            catch (error) {
                exportErrorMessage.value = "The history export request failed. Please try again later.";
            }
            updateExports();
        }
        function onArchiveHistoryWithExport() {
            if (mostUpToDateExport.value) {
                emit("onArchive", mostUpToDateExport.value.id);
            }
        }
        return { __sfc: true, isExportTaskRunning, waitForTask, taskMonitorRequestFailed, taskHasFailed, props, emit, existingExports, isLoading, isExportingRecord, isExportDialogOpen, isDeleteContentsConfirmed, exportErrorMessage, historyName, mostUpToDateExport, canCreateExportRecord, canArchiveHistory, mostUpToDateExportIsReady, updateExports, onCreateExportRecord, doExportToFileSourceWithPrefix, doExportToFileSource, onArchiveHistoryWithExport, BAlert, BButton, BFormCheckbox, BModal, RouterLink, ExportRecordCard, ExportToFileSourceForm, ExportToRDMRepositoryForm, ExternalLink, LoadingSpan };
    }
});
